import { Link } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { ArrowRight, Building2, Users, Clock, Factory, Target, Award, Shield, Truck, Zap, Settings, CheckCircle } from 'lucide-react';
import { PageFAQ } from '@/components/PageFAQ';
import { RelatedPages } from '@/components/RelatedPages';

export default function AboutUs() {
  const stats = [
    { number: '5000+', label: 'Offices Furnished', icon: Building2 },
    { number: '50,000+', label: 'Workstations Delivered', icon: Users },
    { number: '20+', label: 'Years Legacy', icon: Clock },
    { number: '100%', label: 'Made in India', icon: Factory }
  ];

  const values = [
    {
      icon: Zap,
      title: 'Speed & Simplicity',
      description: 'Ready-to-deliver modular furniture designed for fast installation with minimal disruption to your business operations.'
    },
    {
      icon: Settings,
      title: 'Precision Engineering',
      description: 'Every product is built using durable materials and precision processes to perform reliably in demanding work environments.'
    },
    {
      icon: Factory,
      title: 'Factory Direct',
      description: 'Full control over design, engineering, and manufacturing ensures consistent quality, reliable timelines, and exceptional value.'
    },
    {
      icon: CheckCircle,
      title: 'Hassle-Free Setup',
      description: 'Our "Kholo, Jodo, Lagalo" approach eliminates on-site mess, reduces coordination challenges, and gets you working faster.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#f5f3ef]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <SEO 
        title="About Us | Modular Furniture Store - Crafting Workspaces with Legacy and Purpose"
        description="Learn about Modular Furniture Store - backed by 20+ years of manufacturing expertise from Ezeefit Office Systems. Factory direct pricing, 5000+ offices furnished since 2004."
        keywords="about modular furniture store, office furniture company india, B2B furniture manufacturer, workspace solutions, ezeefit"
        canonical="/about"
      />
      
      <PremiumHeader />

      {/* Hero Section */}
      <section className="relative py-24 bg-[#f8f7f4] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#c5a059]/30 to-transparent" />
        </div>
        
        <div className="container mx-auto px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#c5a059] text-sm tracking-[0.3em] uppercase mb-6 font-medium">
              Our Story
            </p>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a1a1a] mb-8 leading-tight"
              
            >
              Crafting Workspaces with
              <br />
              <span className="text-[#c5a059]">Legacy and Purpose</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Modular Furniture Store was created to bring speed, simplicity, and precision to modern workspace creation — backed by decades of manufacturing expertise and a deep understanding of how businesses evolve.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="container mx-auto px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-10 h-10 text-[#c5a059] mx-auto mb-4" />
                <p className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-2" >
                  {stat.number}
                </p>
                <p className="text-sm text-gray-600 tracking-wide uppercase">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-[#f5f3ef]">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="mb-16">
              <p className="text-lg text-gray-700 leading-relaxed">
                Today, we design and deliver modular office furniture that combines thoughtful engineering, refined aesthetics, and practical functionality, helping organizations create efficient workplaces without complexity.
              </p>
            </div>

            {/* Our Journey */}
            <div className="mb-16 bg-white p-10 border-l-4 border-[#c5a059]">
              <h2 
                className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-6"
                
              >
                Our Journey
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our roots trace back to <strong className="text-[#1a1a1a]">Ezeefit Office Systems</strong>, a division of Ezeefit Modular Furniture Pvt. Ltd., which has been manufacturing customizable modular office furniture <strong className="text-[#1a1a1a]">since 2004</strong>. Over the years, the business built strong expertise in designing and producing tailored workspace solutions. In 2014, the company was formalized into a Private Limited entity to better serve the evolving needs of the corporate sector with greater professionalism, scale, and structured processes.
              </p>
              <p className="text-gray-700 leading-relaxed">
                As workplace demands began to change, we observed a growing challenge — companies no longer had the time or flexibility to execute traditional carpentry-based furniture projects on site. These setups often involved coordination complexities, longer timelines, and operational disruptions. Recognizing this shift, Ezeefit Office Systems introduced a smarter approach: <strong className="text-[#1a1a1a]">ready-to-deliver modular furniture</strong> designed for speed, precision, and ease of installation. This vision led to the creation of <strong className="text-[#c5a059]">Modular Furniture Store</strong> — a platform dedicated to helping corporates and startups build offices faster with minimal disruption.
              </p>
            </div>

            {/* Philosophy Section */}
            <div className="mb-16">
              <div className="bg-[#f8f7f4] p-10 border-l-4 border-[#c5a059]">
                <h2 
                  className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-6"
                  
                >
                  The Philosophy — <span className="text-[#c5a059]">"Kholo, Jodo, Lagalo"</span>
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  At the heart of our approach is a simple idea: workspace furniture should be <strong className="text-[#1a1a1a]">easy, efficient, and hassle-free</strong>. Our "Kholo, Jodo, Lagalo" concept reflects our commitment to delivering solutions that eliminate on-site mess, reduce coordination challenges, and allow businesses to focus on what truly matters — <strong className="text-[#c5a059]">growth and expansion</strong>. From ready-to-install workstations to complete office layouts, our systems are designed to move at the pace of modern business.
                </p>
              </div>
            </div>

            {/* Factory Direct Section */}
            <div className="mb-16">
              <h2 
                className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-6"
                
              >
                The Factory Direct Advantage
              </h2>
              <p className="text-gray-700 leading-relaxed">
                With full control over design, engineering, and manufacturing, we ensure consistent quality, reliable timelines, and exceptional value. Every product is built using durable materials and precision processes to perform reliably in demanding work environments. This integrated approach allows us to deliver workspace solutions that are both refined and practical.
              </p>
            </div>

            {/* Commitment */}
            <div className="mb-8 text-center bg-gradient-to-r from-[#c5a059]/10 to-transparent p-10 border border-[#c5a059]/20">
              <h2 
                className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-4"
                
              >
                A Commitment to Excellence
              </h2>
              <p className="text-gray-700 leading-relaxed max-w-2xl mx-auto">
                Our legacy of craftsmanship, combined with a forward-looking mindset, continues to guide everything we do. The trust of thousands of organizations across industries reflects our dedication to quality, reliability, and long-term partnerships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a059] text-sm tracking-[0.3em] uppercase mb-4 font-medium">
              What We Deliver
            </p>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4"
              
            >
              Why Choose Us
            </h2>
            <div className="w-24 h-1 bg-[#c5a059] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <div key={index} className="text-center p-8 bg-[#f8f7f4] hover:bg-[#c5a059]/10 hover:border-[#c5a059] border border-transparent group transition-all duration-300">
                <value.icon className="w-12 h-12 text-[#c5a059] mx-auto mb-6" />
                <h3 className="text-lg font-bold text-[#1a1a1a] mb-4 transition-colors">
                  {value.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed transition-colors">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#f8f7f4]">
        <div className="container mx-auto px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6"
              
            >
              Let's Build Your
              <span className="text-[#c5a059]"> Next Workspace</span>
            </h2>
            <p className="text-gray-600 mb-10 text-lg leading-relaxed">
              Whether you are launching a new office, expanding your team, or optimizing an existing space, we are here to help you create a workplace that supports your ambitions — with speed, simplicity, and confidence.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/bulk-inquiry">
                <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a] rounded-none px-10 py-6 text-sm tracking-[0.15em] font-bold transition-all hover:translate-y-[-2px]">
                  GET FREE LAYOUT DESIGN
                  <ArrowRight className="ml-3 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white rounded-none px-10 py-6 text-sm tracking-[0.15em] font-bold transition-all bg-transparent">
                  CONTACT US
                </Button>
              </Link>
            </div>
            
            {/* Tagline */}
            <p className="mt-12 text-[#c5a059] text-lg font-medium italic">
              Built on Experience. Designed for Modern Work.
            </p>
          </div>
        </div>
      </section>

      {/* Footer - Dark */}
      <footer className="bg-[#1a1a1a] text-white py-16 border-t border-gray-800">
        <div className="container mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <img 
                src="/mfs-logo.png"
                alt="Modular Furniture Store"
                className="h-16 w-auto mb-4"
              />
              <p className="text-gray-400 mb-4 max-w-md">
                Premium "Office Furniture Store" by Ezeefit. 
                Furnishing India's finest Offices since 2004.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-[#c5a059]">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm">GST Registered</span>
                </div>
                <div className="flex items-center gap-2 text-[#c5a059]">
                  <Award className="w-4 h-4" />
                  <span className="text-sm">Made in India</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold mb-4 text-[#c5a059] tracking-wider text-sm">QUICK LINKS</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/products" className="hover:text-white transition-colors">All Products</Link></li>
                <li><Link to="/bulk-inquiry" className="hover:text-white transition-colors">Bulk Inquiry</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Knowledge Hub</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-bold mb-4 text-[#c5a059] tracking-wider text-sm">CONTACT</h4>
              <ul className="space-y-2 text-gray-400">
                <li>hello@modularfurniturestore.com</li>
                <li>+91 9594685818</li>
                <li>+91 9820042100</li>
                <li className="pt-2">Mumbai, Maharashtra</li>
              </ul>
            </div>
          </div>

      {/* FAQ + Interlinks */}
      <div className="container mx-auto px-4 sm:px-8 py-12 bg-white">
        <PageFAQ pageKey="about" />
        <RelatedPages pageKey="about" className="mt-12" />
      </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © 2024 Modular Furniture Store by Ezeefit. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-gray-500 text-sm">
              <Link to="/policies" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/policies" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
